import { useState } from 'react';

import { createSupportIssue } from '@commenda/api';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Input,
  Label,
  LoadingIndicator,
  Textarea,
  toast,
  useToggle,
} from '@commenda/kakashi';

import { useConfig } from 'context/useConfig';

interface HelpAndSupportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SUBJECT_MAX_LENGTH = 200; // gmail truncates after ~70 chars, so 200 is generous
const MESSAGE_MAX_LENGTH = 10000;

const HelpAndSupportModal = ({ isOpen, onClose }: HelpAndSupportModalProps) => {
  const { companyId } = useConfig();

  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const isSubmitting = useToggle(false);

  const resetFields = () => {
    setSubject('');
    setMessage('');
  };

  const handleClose = () => {
    resetFields();
    isSubmitting.stop();
    onClose();
  };

  const isSubmitDisabled =
    !subject.trim() || !message.trim() || !companyId || isSubmitting.value;

  const handleSubmit = async () => {
    if (!companyId) return;

    isSubmitting.start();
    const result = await createSupportIssue({
      companyId,
      subject: subject.trim(),
      message: message.trim(),
    });
    isSubmitting.stop();

    if (!result.success) {
      toast.error(
        result.error ||
          'Failed to send your request. Please email us at operations@commenda.io.'
      );
      return;
    }

    toast.success(
      'Support request sent. Our team will get back to you shortly.'
    );
    resetFields();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent preventCloseOnOutsideClick>
        <DialogHeader className="flex flex-col items-start">
          <DialogTitle>Help and support</DialogTitle>
          <DialogDescription>
            Describe your issue and our team will get back to you. You can also
            email us at{' '}
            <span className="font-semibold">operations@commenda.io</span>.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-xl p-3xl">
          <FieldWrapper>
            <Label required>Subject</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={SUBJECT_MAX_LENGTH}
              placeholder="Briefly summarize your issue"
              disabled={isSubmitting.value}
            />
          </FieldWrapper>

          <FieldWrapper>
            <Label required>Message</Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={MESSAGE_MAX_LENGTH}
              placeholder="Tell us what's going on..."
              disabled={isSubmitting.value}
            />
          </FieldWrapper>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="secondary" size="sm" disabled={isSubmitting.value}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="primary"
            size="sm"
            disabled={isSubmitDisabled}
            onClick={handleSubmit}
          >
            {isSubmitting.value ? <LoadingIndicator /> : null} Send request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { HelpAndSupportModal };
